/**
 * Primitivas de las pantallas con «estructura de Perfil» (DESIGN.md §3; mockup `.hdr`, `.two`, `.srow`, `.chip`,
 * `.toggle`): Favoritos, Alertas y Cobertura. Clases de Tailwind de la paleta existente; nada de color nuevo.
 */
import { ReactNode } from 'react';

export const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600';

/** Fondo blanco a todo el alto bajo la cabecera (anula el relleno de `Layout`). Con sesión, la columna arranca en la
 * misma x que el título de la cabecera (DESIGN.md §11.1); sin sesión (Cobertura pública) va centrada. */
export function Columna({ children, ancho = 880, sinLayout = false }: { children: ReactNode; ancho?: number; sinLayout?: boolean }) {
  return (
    <div className={sinLayout ? 'bg-white' : '-m-4 lg:-m-8 min-h-[calc(100vh-85px)] bg-white'}>
      <div className={sinLayout ? 'px-7 py-6' : 'px-4 lg:px-8 py-6'}>
        <div className={sinLayout ? 'mx-auto w-full' : 'w-full'} style={{ maxWidth: ancho }}>{children}</div>
      </div>
    </div>
  );
}

/** Encabezado con ícono en círculo, título, una línea de ayuda y acciones a la derecha (`.hdr`). */
export function Encabezado({ icono, titulo, subtitulo, derecha }: { icono: ReactNode; titulo: ReactNode; subtitulo?: ReactNode; derecha?: ReactNode }) {
  return (
    <div className="flex items-center gap-3.5 mb-[18px]">
      <span className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 inline-flex items-center justify-center flex-none" aria-hidden="true">
        {icono}
      </span>
      <div className="min-w-0">
        <h1 className="text-base font-semibold text-slate-900">{titulo}</h1>
        {subtitulo && <div className="text-[13px] text-slate-500 mt-px">{subtitulo}</div>}
      </div>
      {derecha && <div className="ml-auto flex items-center gap-2.5 flex-wrap justify-end">{derecha}</div>}
    </div>
  );
}

/** Dos columnas con 32 px entre ellas (`.two`). */
export function DosColumnas({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`grid grid-cols-2 gap-x-8 ${className}`}>{children}</div>;
}

/** Ceja de grupo (`.t-eyebrow`): solo encabezados de grupo. */
export function Ceja({ children }: { children: ReactNode }) {
  return <h2 className="text-[11px] font-semibold tracking-[.06em] uppercase text-slate-400 mb-0.5">{children}</h2>;
}

/**
 * Fila de ajuste (`.srow`): título, ayuda y acciones a la derecha. `enHover` son acciones que aparecen al pasar el
 * mouse o con el foco del teclado, con su espacio reservado (DESIGN.md §1.3: nada se mueve al aparecer).
 */
export function Fila({ titulo, ayuda, acciones, enHover, unaLinea = false, className = '' }: { titulo: ReactNode; ayuda?: ReactNode; acciones?: ReactNode; enHover?: ReactNode; unaLinea?: boolean; className?: string }) {
  return (
    <div className={`group flex justify-between items-center gap-3 py-3.5 border-t border-slate-100 text-[13.5px] ${className}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 font-medium text-slate-900 min-w-0">{titulo}</div>
        {ayuda && <div className={`text-xs text-slate-500 mt-0.5 ${unaLinea ? 'truncate' : ''}`}>{ayuda}</div>}
      </div>
      {(acciones || enHover) && (
        <div className="flex items-center gap-3 flex-none">
          {enHover && (
            <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 motion-reduce:transition-none">
              {enHover}
            </div>
          )}
          {acciones}
        </div>
      )}
    </div>
  );
}

const ACCION = `inline-flex items-center gap-1 text-[13px] whitespace-nowrap rounded ${FOCO}`;

/** Acción de fila (`.srow .act`). `fantasma` = visible solo al pasar el mouse o con foco, con su espacio reservado. */
export function accionClase({ fantasma = false, deshabilitada = false } = {}) {
  const color = deshabilitada ? 'text-slate-400 cursor-not-allowed' : 'text-slate-600 hover:text-slate-900';
  const vis = fantasma ? 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150 motion-reduce:transition-none' : '';
  return `${ACCION} ${color} ${vis}`;
}

type Tono = 'ty' | 'ok' | 'warn' | 'bad' | 'info' | 'neutro';
const TONOS: Record<Tono, string> = {
  ty: 'bg-indigo-50 text-indigo-700',
  ok: 'bg-emerald-50 text-emerald-700',
  warn: 'bg-amber-50 text-amber-700',
  bad: 'bg-red-50 text-red-700',
  info: 'bg-blue-50 text-blue-700',
  neutro: 'bg-slate-100 text-slate-600',
};

/** Chip (`.chip`): tipo de documento (índigo) o estado (verde, ámbar, rojo, azul). */
export function Chip({ tono = 'neutro', children }: { tono?: Tono; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 h-5 px-[7px] rounded-md text-[11px] font-medium whitespace-nowrap flex-none ${TONOS[tono]}`}>
      {children}
    </span>
  );
}

/** Chip de filtro (`.chip.f`). */
export function ChipFiltro({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={`h-[26px] px-2.5 rounded-full border text-[12.5px] whitespace-nowrap ${FOCO} ${activo
        ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-medium'
        : 'bg-white border-gray-200 text-slate-600 hover:bg-slate-50'}`}
    >
      {children}
    </button>
  );
}

/** Interruptor (`.toggle`): se mueve en 180 ms; sin movimiento con `prefers-reduced-motion`. */
export function Interruptor({ encendido, onChange, etiqueta, deshabilitado = false }: { encendido: boolean; onChange: (v: boolean) => void; etiqueta: string; deshabilitado?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={encendido}
      aria-label={etiqueta}
      disabled={deshabilitado}
      onClick={() => onChange(!encendido)}
      className={`relative w-9 h-5 rounded-full flex-none transition-colors duration-[180ms] ease-out motion-reduce:transition-none ${FOCO} ${encendido ? 'bg-indigo-600' : 'bg-slate-300'} ${deshabilitado ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        aria-hidden="true"
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white perilla transition-[left] duration-[180ms] motion-reduce:transition-none ${encendido ? 'left-[18px]' : 'left-0.5'}`}
      />
    </button>
  );
}

/** Barra gris de relleno para el estado «cargando» (estructura sin datos, nunca un spinner de página). */
export function Relleno({ ancho = '60%' }: { ancho?: string }) {
  return <span className="block h-3 rounded bg-slate-100" style={{ width: ancho }} aria-hidden="true" />;
}
